import { Cog, ExternalLink, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useGenerateNotificationDocument, useInitClaim } from "../../api/use-claim-actions";
import { CtaShell } from "./cta-shell";

/** Port of states-cta/StartClaimCTA.vue — UPCLAIM.WAIT_FOR_1ST_EMAIL_SEND. */
export function StartClaimCTA({ claim }: { claim: ClaimWithRelations }) {
  const generateNotificationDocument = useGenerateNotificationDocument(claim.id);
  const initClaim = useInitClaim(claim.id);

  const hasNotification = claim.claimDocuments.some(
    ({ documentType: { type } }) => type === "CLAIM_NOTIFICATION",
  );
  const frontConversationId = claim.conversation?.frontConversationId;
  const hasConversation = Boolean(frontConversationId);

  const items = [
    {
      name: "Générer la notification",
      description: "Notification de cession de créance à destination de la compagnie aérienne.",
      Icon: Cog,
      cta: {
        text: hasNotification ? "Re-Générer Notification" : "Générer Notification",
        disabled: generateNotificationDocument.isPending,
        handleClick: () => generateNotificationDocument.mutate(),
      },
    },
    {
      name: "Envoie du premier email",
      description: "Envoyer la première tentative de conciliation amiable à la compagnie.",
      Icon: hasConversation ? ExternalLink : Mail,
      cta: hasConversation
        ? {
            text: "Ouvrir dans Front",
            disabled: false,
            handleClick: () =>
              window.open(`https://app.frontapp.com/open/${frontConversationId}`, "_blank")?.focus(),
          }
        : {
            text: "Générer le Brouillon",
            disabled: initClaim.isPending,
            handleClick: () => initClaim.mutate(),
          },
    },
  ];

  return (
    <CtaShell legalStepName={claim.claimLegalStep.name}>
      <p className="mt-1 text-sm text-gray-500">Quelques étapes sont nécessaires pour démarrer le dossier.</p>
      <ul className="mt-6 divide-y divide-gray-200 border-t border-b border-gray-200">
        {items.map((item, itemIdx) => (
          <li key={item.name}>
            <div className="group pointer-events-auto relative flex items-start space-x-6 py-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {itemIdx + 1}. {item.name}
                </p>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
              <div className="shrink-0 self-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={item.cta.handleClick}
                  disabled={item.cta.disabled}
                >
                  <item.Icon />
                  {item.cta.text}
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </CtaShell>
  );
}
